import React from 'react';
import { buildCharacterSpellbookViewModel } from '../utils/CharacterSpellbookPresentation';
import './CharacterSpellbookEditor.css';

const ABILITIES = ['intelligence', 'wisdom', 'charisma'];

const titleCase = (value) => String(value || '').replace(/^./, (letter) => letter.toUpperCase());
const signed = (value) => value == null ? '' : `${value >= 0 ? '+' : ''}${value}`;

const formatDamage = (damage) => (Array.isArray(damage) ? damage : [damage])
  .filter(Boolean)
  .map((entry) => typeof entry === 'string'
    ? entry
    : `${entry?.dice || entry?.formula || ''} ${entry?.type || entry?.damageType || ''}`.trim())
  .filter(Boolean)
  .join(', ');

const formatRange = (range) => {
  if (typeof range === 'string' || typeof range === 'number') return String(range);
  if (!range || typeof range !== 'object' || Array.isArray(range)) return '';
  const normal = Number(range.normal);
  const long = Number(range.long);
  if (Number.isFinite(normal) && Number.isFinite(long) && long > normal) return `${normal}/${long} ft.`;
  if (Number.isFinite(normal)) return `${normal} ft.`;
  return String(range.type || range.unit || '');
};

const formatComponents = (components) => {
  if (typeof components === 'string') return components;
  if (Array.isArray(components)) return components.join(', ');
  if (!components || typeof components !== 'object') return '';
  return [
    components.verbal || components.v ? 'V' : '',
    components.somatic || components.s ? 'S' : '',
    components.material || components.m ? 'M' : ''
  ].filter(Boolean).join(', ');
};

const getProficiencyBonus = (subject) => {
  const explicit = Number(subject?.proficiencyBonus);
  if (Number.isFinite(explicit)) return explicit;
  const level = Math.max(1, Number(subject?.level) || 1);
  return Math.floor((level - 1) / 4) + 2;
};

const CharacterSpellbookEditor = ({
  subject,
  spellData,
  onAbilityChange,
  onAdjustmentChange,
  onSlotChange,
  onSpellChange,
  onAddSpell,
  onRemoveSpell
}) => {
  const model = buildCharacterSpellbookViewModel(spellData, subject);
  const ability = String(spellData?.ability || '').toLowerCase();
  const proficiencyBonus = getProficiencyBonus(subject);
  const baseAttack = model.modifier == null ? null : model.modifier + proficiencyBonus;
  const baseSaveDC = model.modifier == null ? null : 8 + model.modifier + proficiencyBonus;
  const attackAdjustment = baseAttack == null || model.attackBonus == null ? 0 : model.attackBonus - baseAttack;
  const saveAdjustment = baseSaveDC == null || model.saveDC == null ? 0 : model.saveDC - baseSaveDC;
  const entries = Array.isArray(spellData?.entries) ? spellData.entries : [];

  return (
    <section className="character-spellbook-editor" aria-labelledby="character-spellbook-editor-title">
      <header className="character-spellbook-editor__header">
        <div>
          <p>Spellcasting Record</p>
          <h2 id="character-spellbook-editor-title">Spellbook</h2>
        </div>
        <button type="button" onClick={onAddSpell}>Add spell</button>
      </header>

      <div className="character-spellbook-editor__stats">
        <label>
          <span>Ability</span>
          <select value={ability} aria-label="Spellcasting ability" onChange={(event) => onAbilityChange(event.target.value)}>
            <option value="">Select</option>
            {ABILITIES.map((entry) => <option key={entry} value={entry}>{titleCase(entry)}</option>)}
          </select>
        </label>
        <label>
          <span>Modifier</span>
          <input
            type="text"
            value={signed(model.modifier)}
            readOnly
            title="Calculated from the selected ability score."
            aria-label="Spellcasting ability modifier"
          />
        </label>
        <label>
          <span>Save Adjustment</span>
          <input
            type="number"
            value={saveAdjustment}
            aria-label="Spell save DC adjustment"
            onChange={(event) => onAdjustmentChange('saveDC', parseInt(event.target.value, 10) || 0)}
          />
        </label>
        <label>
          <span>Save DC</span>
          <input
            type="number"
            value={model.saveDC ?? ''}
            readOnly
            title="Calculated from ability, proficiency, and save adjustment."
            aria-label="Spell save DC"
          />
        </label>
        <label>
          <span>Attack Adjustment</span>
          <input
            type="number"
            value={attackAdjustment}
            aria-label="Spell attack adjustment"
            onChange={(event) => onAdjustmentChange('attackBonus', parseInt(event.target.value, 10) || 0)}
          />
        </label>
        <label>
          <span>Attack Bonus</span>
          <input
            type="text"
            value={signed(model.attackBonus)}
            readOnly
            title="Calculated from ability, proficiency, and attack adjustment."
            aria-label="Spell attack bonus"
          />
        </label>
      </div>

      <section className="character-spellbook-editor__slots" aria-labelledby="spell-slot-editor-title">
        <div className="character-spellbook-editor__section-heading">
          <div>
            <h3 id="spell-slot-editor-title">Spell Slots</h3>
            <p>Track spent slots and the maximum available at each level.</p>
          </div>
          <span>Used / Max</span>
        </div>
        <div className="character-spellbook-editor__slot-grid">
          {Array.from({ length: 9 }, (_, index) => index + 1).map((level) => {
            const slot = spellData?.slots?.[level] || spellData?.slots?.[String(level)] || {};
            return (
              <div className="character-spellbook-editor__slot" key={level}>
                <strong>Level {level}</strong>
                <input
                  type="number"
                  min="0"
                  value={slot.used ?? 0}
                  aria-label={`Level ${level} spell slots used`}
                  onChange={(event) => onSlotChange(level, 'used', event.target.value)}
                />
                <span>/</span>
                <input
                  type="number"
                  min="0"
                  value={slot.total ?? slot.max ?? 0}
                  aria-label={`Level ${level} maximum spell slots`}
                  onChange={(event) => onSlotChange(level, 'total', event.target.value)}
                />
              </div>
            );
          })}
        </div>
      </section>

      <section className="character-spellbook-editor__spells" aria-labelledby="recorded-spells-title">
        <div className="character-spellbook-editor__section-heading">
          <div>
            <h3 id="recorded-spells-title">Recorded Spells</h3>
            <p>Keep full rules, source, and character-specific notes with each spell.</p>
          </div>
          <span>{entries.length} spell{entries.length === 1 ? '' : 's'}</span>
        </div>

        {entries.length === 0 ? (
          <p className="character-spellbook-editor__empty">No spells recorded.</p>
        ) : (
          <div className="character-spellbook-editor__spell-list">
            {entries.map((spell, index) => {
              const name = String(spell?.name || spell?.title || 'Unnamed spell');
              const level = Math.max(0, Math.min(9, parseInt(spell?.level, 10) || 0));
              return (
                <details className="character-spellbook-editor__spell" key={spell?.id || `spell-${index}`}>
                  <summary>
                    <span>{name}</span>
                    <small>{level === 0 ? 'Cantrip' : `Level ${level}`}{level > 0 && spell?.prepared ? ' · Prepared' : ''}</small>
                  </summary>
                  <div className="character-spellbook-editor__spell-fields">
                    <label className="character-spellbook-editor__field--wide">
                      <span>Name</span>
                      <input
                        type="text"
                        value={name === 'Unnamed spell' ? '' : name}
                        aria-label={`Spell ${index + 1} name`}
                        onChange={(event) => onSpellChange(index, { name: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Level</span>
                      <select
                        value={level}
                        aria-label={`${name} level`}
                        onChange={(event) => onSpellChange(index, { level: parseInt(event.target.value, 10) || 0 })}
                      >
                        <option value="0">Cantrip</option>
                        {Array.from({ length: 9 }, (_, itemIndex) => itemIndex + 1).map((spellLevel) => (
                          <option value={spellLevel} key={spellLevel}>Level {spellLevel}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>School</span>
                      <input
                        type="text"
                        value={spell?.school || ''}
                        placeholder="Illusion"
                        aria-label={`${name} school`}
                        onChange={(event) => onSpellChange(index, { school: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Source</span>
                      <input
                        type="text"
                        value={spell?.acquisition || spell?.grantedBy || (typeof spell?.source === 'string' ? spell.source : spell?.source?.name) || ''}
                        placeholder="Wizard spell"
                        aria-label={`${name} source`}
                        onChange={(event) => onSpellChange(index, { acquisition: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Casting Time</span>
                      <input
                        type="text"
                        value={spell?.castingTime || ''}
                        placeholder="1 action"
                        aria-label={`${name} casting time`}
                        onChange={(event) => onSpellChange(index, { castingTime: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Range</span>
                      <input
                        type="text"
                        value={formatRange(spell?.range)}
                        placeholder="60 ft."
                        aria-label={`${name} range`}
                        onChange={(event) => onSpellChange(index, { range: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Duration</span>
                      <input
                        type="text"
                        value={spell?.duration || ''}
                        placeholder="Instantaneous"
                        aria-label={`${name} duration`}
                        onChange={(event) => onSpellChange(index, { duration: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Components</span>
                      <input
                        type="text"
                        value={formatComponents(spell?.components)}
                        placeholder="V, S, M"
                        aria-label={`${name} components`}
                        onChange={(event) => onSpellChange(index, { components: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Damage</span>
                      <input
                        type="text"
                        value={formatDamage(spell?.damage)}
                        placeholder="2d6 psychic"
                        aria-label={`${name} damage`}
                        onChange={(event) => onSpellChange(index, { damage: event.target.value })}
                      />
                    </label>
                    <label>
                      <span>Save</span>
                      <select
                        value={String(spell?.saveType || spell?.savingThrow?.ability || spell?.save?.ability || '').toLowerCase()}
                        aria-label={`${name} saving throw`}
                        onChange={(event) => onSpellChange(index, {
                          saveType: event.target.value,
                          savingThrow: {
                            ...(spell?.savingThrow && typeof spell.savingThrow === 'object' ? spell.savingThrow : {}),
                            ability: event.target.value
                          }
                        })}
                      >
                        <option value="">None</option>
                        {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map((entry) => (
                          <option key={entry} value={entry}>{entry.slice(0, 3).toUpperCase()}</option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="character-spellbook-editor__spell-flags">
                    {level > 0 && (
                      <label><input type="checkbox" checked={Boolean(spell?.prepared)} onChange={(event) => onSpellChange(index, { prepared: event.target.checked })} /> Prepared</label>
                    )}
                    <label><input type="checkbox" checked={Boolean(spell?.ritual)} onChange={(event) => onSpellChange(index, { ritual: event.target.checked })} /> Ritual</label>
                    <label><input type="checkbox" checked={Boolean(spell?.concentration)} onChange={(event) => onSpellChange(index, { concentration: event.target.checked })} /> Concentration</label>
                  </div>

                  <label className="character-spellbook-editor__text-field">
                    <span>Material</span>
                    <textarea rows={2} value={spell?.material || spell?.materialComponent || ''} onChange={(event) => onSpellChange(index, { material: event.target.value })} />
                  </label>
                  <label className="character-spellbook-editor__text-field">
                    <span>Character Effect</span>
                    <textarea rows={2} value={spell?.modification || ''} onChange={(event) => onSpellChange(index, { modification: event.target.value })} />
                  </label>
                  <label className="character-spellbook-editor__text-field">
                    <span>Rules & Notes</span>
                    <textarea rows={5} value={spell?.description || spell?.notes || ''} onChange={(event) => onSpellChange(index, { description: event.target.value })} />
                  </label>
                  <button type="button" className="character-spellbook-editor__remove" onClick={() => onRemoveSpell(index)}>
                    Remove {name}
                  </button>
                </details>
              );
            })}
          </div>
        )}
      </section>
    </section>
  );
};

export default CharacterSpellbookEditor;
